import React, { useEffect, useState } from "react";
import clsx from "clsx";
import { lighten, Theme } from "@mui/material/styles";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Toolbar,
  Typography,
  Paper,
  Grid,
  IconButton,
  Tooltip,
} from "@mui/material";

import {
  gray1,
  lighestGray,
  lightGray,
  pprBorderColorA,
  strongGray,
  textColor,
  textGrayH1,
  white,
} from "../DashStyleGuides";
import { GetTopOrdersToShip } from "../ApiServices/serviceCalls";

import { DispatchProduct } from "../../../models/dispatch.product";
import { DispatchDestinationAddress } from "../../../models/dispatch.destination.address";
import { DispatchOriginAddress } from "../../../models/dispatch.origin.address";

import { formatInTimeZone } from "../../../util/formatInTimezone";

interface OrdersToShipData {
  order_po: string;
  despatch_date: string;
  status: string;
  sender_address: string;
  delivery_address: string;
  receiver: string;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: "100%",
      height: 300,
      borderRadius: 12,
    },
    paper: {
      width: "100%",
      marginBottom: theme.spacing(2),
      border: `1px solid ${pprBorderColorA}`,
      borderRadius: 12,
      "& .MuiTableContainer-root": {
        height: 250,
      },
    },
    table: {
      minWidth: 632,
      "& .tblrow": {
        height: 44,
        fontSize: 12,
        fontFamily: "Poppins",
        fontWeight: 400,
        color: textColor,
      },
      "& .light": {
        background: white,
      },
      "& .darker": { background: lightGray },
    },
    visuallyHidden: {
      border: 0,
      clip: "rect(0 0 0 0)",
      height: 1,
      margin: -1,
      overflow: "hidden",
      padding: 0,
      position: "absolute",
      top: 20,
      width: 1,
    },
    tblPage: {
      maxWidth: 635,
      "& .MuiToolbar-gutters": { padding: 0 },
    },
    tblHeaderRoot: {
      height: 32,
      "& .MuiTableCell-stickyHeader": {
        background: lightGray,
      },

      "& th": {
        fontSize: 12,
        fontWeight: 700,
        color: textGrayH1,
      },
    },
    lblEmpty: {
      display: "block",
      margin: "auto",
      marginTop: 80,
      color: pprBorderColorA,
      fontWeight: 400,
    },
  })
);

const useToolbarStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(1),
      borderBottom: `1px solid ${pprBorderColorA}`,
      borderRadius: "12px 12px 0px 0px",
    },
    highlight:
      theme.palette.mode === "light"
        ? {
            color: theme.palette.secondary.main,
            backgroundColor: lighten(theme.palette.secondary.light, 0.85),
          }
        : {
            color: theme.palette.text.primary,
            backgroundColor: theme.palette.secondary.dark,
          },
    title: {
      flex: "1 1 100%",
      fontFamily: "Manrope",
      fontStyle: "normal",
      fontWeight: "bold",
      fontSize: 16,
      color: strongGray,
    },
  })
);

type Order = "asc" | "desc";

interface HeadCell {
  disablePadding: boolean;
  id: keyof OrdersToShipData;
  label: string;
  numeric: boolean;
}

const headCells: HeadCell[] = [
  {
    id: "order_po",
    numeric: true,
    disablePadding: true,
    label: "Order",
  },
  {
    id: "despatch_date",
    numeric: true,
    disablePadding: false,
    label: "Despatch Date",
  },
  { id: "status", numeric: true, disablePadding: false, label: "Status" },
  {
    id: "sender_address",
    numeric: false,
    disablePadding: false,
    label: "Sender Address",
  },
  {
    id: "delivery_address",
    numeric: false,
    disablePadding: false,
    label: "Delivery Address",
  },
  { id: "receiver", numeric: false, disablePadding: false, label: "Receiver" },
];

function EnhancedTableHead() {
  const cssClasses = useStyles();
  return (
    <TableHead className={cssClasses.tblHeaderRoot}>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell key={headCell.id} align="center" padding="none">
            {headCell.label}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

const EnhancedTableToolbar = () => {
  const classes = useToolbarStyles();

  return (
    <Toolbar className={classes.root} variant="dense">
      <Typography className={classes.title} id="tableTitle" component="div">
        Orders To Ship
      </Typography>

      {/* <Tooltip title="More options">
        <IconButton aria-label="More options">
          <MoreVertRounded />
        </IconButton>
      </Tooltip> */}
    </Toolbar>
  );
};

/** Default Functional Component */
export default function OrdersToShip() {
  const classes = useStyles();

  const [ordersToShipList, setOrdersToShipList] = useState<DispatchProduct[]>([]);
  useEffect(() => {
    getTopList();
  }, []);

  const getTopList = async () => {
    const ordersToShip: any = await GetTopOrdersToShip();
    if (ordersToShip) {
      setOrdersToShipList(ordersToShip !== undefined ? ordersToShip.data : undefined);
    }
  };

  const getDispatchAddress = (
    dispatchAddress: DispatchOriginAddress | DispatchDestinationAddress
  ) => {
    if (dispatchAddress?.state && dispatchAddress?.suburb && dispatchAddress?.country) {
      return `${dispatchAddress?.suburb}, ${dispatchAddress?.state}, ${dispatchAddress?.country}`;
    }

    if (dispatchAddress?.state && dispatchAddress?.suburb) {
      return `${dispatchAddress?.suburb}, ${dispatchAddress?.state}`;
    }

    if (dispatchAddress?.suburb && dispatchAddress?.country) {
      return `${dispatchAddress?.suburb}, ${dispatchAddress?.country}`;
    }

    if (dispatchAddress?.state && dispatchAddress?.country) {
      return `${dispatchAddress?.state}, ${dispatchAddress?.country}`;
    }

    return ``;
  };

  return (
    <Grid item sm={12} md={8} xs={12}>
      <div className={classes.root}>
        <Paper className={classes.paper}>
          <EnhancedTableToolbar />
          <TableContainer>
            <Table
              className={classes.table}
              aria-labelledby="tableTitle"
              size="small"
              aria-label="enhanced table"
              stickyHeader
            >
              <EnhancedTableHead />
              <TableBody>
                {ordersToShipList.length > 0 &&
                  ordersToShipList.map((info, index) => (
                    <TableRow
                      className={`tblrow ${index % 2 == 0 ? "light" : "darker"}`}
                      hover
                      role="checkbox"
                      tabIndex={-1}
                      key={index}
                    >
                      <TableCell
                        id={info?.id?.toString() || index?.toString()}
                        scope="row"
                        padding="none"
                        align="center"
                      >
                        {info?.purchaseOrderId}
                      </TableCell>
                      <TableCell scope="row" padding="none" align="center">
                        {formatInTimeZone(
                          "dd/MM/yyyy HH:mm a",
                          Intl.DateTimeFormat().resolvedOptions().timeZone,
                          info?.dispatch?.createdAt
                        )}
                      </TableCell>
                      <TableCell align="center">{info?.status ? info.status : "-"}</TableCell>
                      <TableCell align="center">
                        {info?.dispatch?.dispatchOriginAddresses &&
                        info?.dispatch?.dispatchOriginAddresses?.length > 0
                          ? getDispatchAddress(info?.dispatch?.dispatchOriginAddresses[0])
                          : "-"}
                      </TableCell>
                      <TableCell align="center">
                        {info?.dispatch?.dispatchDestinationAddresses &&
                        info?.dispatch?.dispatchDestinationAddresses?.length > 0
                          ? getDispatchAddress(info?.dispatch?.dispatchDestinationAddresses[0])
                          : "-"}
                      </TableCell>
                      <TableCell align="center">
                        {info?.dispatch?.dispatchDestinationAddresses &&
                        info?.dispatch?.dispatchDestinationAddresses?.length > 0
                          ? info?.dispatch?.dispatchDestinationAddresses[0]?.name
                          : "-"}
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
            {ordersToShipList.length === 0 && (
              <Typography className={classes.lblEmpty} variant="h4" align="center">
                No Data
              </Typography>
            )}
          </TableContainer>
        </Paper>
      </div>
    </Grid>
  );
}
